import {useEffect, useRef} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  KEY_ENTER_COMMAND,
} from 'lexical';

import {$createPageNode, $createPageNodeWithHeaderFooter, $isPageNode} from '../../nodes/PageNode';

function px(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function getBodyEl(pageEl) {
  return pageEl.querySelector('[data-page-body="true"]');
}

function getBodyMaxHeight(bodyEl) {
  const cs = window.getComputedStyle(bodyEl);
  return bodyEl.clientHeight - px(cs.paddingTop) - px(cs.paddingBottom);
}

function getBlockOuterHeight(editor, node) {
  const el = editor.getElementByKey(node.getKey());
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  const cs = window.getComputedStyle(el);
  return Math.ceil(rect.height + px(cs.marginTop) + px(cs.marginBottom));
}

function getUsedHeight(editor, page) {
  const body = page.getBody();
  if (!body) return 0;
  let used = 0;
  for (const c of body.getChildren()) used += getBlockOuterHeight(editor, c);
  return used;
}

function getLineHeightPx(el) {
  const cs = window.getComputedStyle(el);
  const lh = cs.lineHeight;
  if (lh.endsWith('px')) return px(lh);
  const fs = px(cs.fontSize);
  return fs > 0 ? fs * 1.2 : 18;
}

function getParentPage(node) {
  let n = node;
  while (n) {
    if ($isPageNode(n)) return n;
    n = n.getParent();
  }
  return null;
}

function getParentParagraph(node) {
  let n = node;
  while (n) {
    if ($isParagraphNode(n)) return n;
    n = n.getParent?.();
  }
  return null;
}

function getParentRegionType(node) {
  let n = node;
  while (n) {
    const t = typeof n.getType === 'function' ? n.getType() : null;
    if (t === 'page_header') return 'header';
    if (t === 'page_body') return 'body';
    if (t === 'page_footer') return 'footer';
    n = n.getParent?.();
  }
  return null;
}

function ensureNextPage(pages, root, index) {
  let next = pages[index + 1];
  if (!next) {
    // Copy header/footer from the current page
    const currentPage = pages[index];
    next = $createPageNodeWithHeaderFooter(currentPage);
    root.append(next);
  }
  return next;
}

function prependToBody(page, node) {
  const body = page.getBody();
  if (!body) return;
  const first = body.getFirstChild();
  if (first) first.insertBefore(node);
  else body.append(node);
}

function ensureParagraphHasText(p) {
  const kids = p.getChildren?.();
  const hasText = kids?.some((k) => $isTextNode(k)) ?? false;
  if (!kids || kids.length === 0 || !hasText) {
    p.append?.($createTextNode(''));
  }
}

function selectParagraphStart(p) {
  ensureParagraphHasText(p);
  const kids = p.getChildren?.();
  if (kids) {
    for (const k of kids) {
      if ($isTextNode(k)) {
        k.select(0, 0);
        return;
      }
    }
  }
  p.selectStart?.();
}

function selectEndOfBody(page) {
  const body = page.getBody();
  if (!body) return;

  const last = body.getLastChild();
  if (!last) {
    const p = $createParagraphNode();
    p.append($createTextNode(''));
    body.append(p);
    selectParagraphStart(p);
    return;
  }

  if ($isParagraphNode(last)) {
    ensureParagraphHasText(last);
    last.selectEnd?.();
    return;
  }

  last.selectEnd?.();
}

function isBodyEffectivelyEmpty(page) {
  const body = page.getBody();
  if (!body) return true;
  const text = body.getTextContent().replace(/\u200B/g, '').trim();
  if (text !== '') return false;
  return body.getChildrenSize() <= 1;
}

function isEmptyParagraph(node) {
  return (
    $isParagraphNode(node) &&
    (node.getTextContent?.() ?? '').replace(/\u200B/g, '').trim() === ''
  );
}

/**
 * Safely removes a page by first clearing its children to prevent orphaned nodes.
 * This avoids duplicated header/footer artifacts that can occur when region nodes
 * become detached during page removal.
 */
function safeRemovePage(page) {
  if (!page || typeof page.remove !== 'function') return;
  
  // Clear all children first to prevent orphaned region nodes
  const children = page.getChildren();
  for (const child of children) {
    child.remove();
  }
  
  // Now safely remove the empty page
  page.remove();
}

function cleanupEmptyLeadingPagesAndFixSelection() {
  const root = $getRoot();
  let pages = root.getChildren().filter($isPageNode);
  if (pages.length <= 1) return;

  while (pages.length > 1) {
    const first = pages[0];
    const firstEmpty = isBodyEffectivelyEmpty(first);
    if (!firstEmpty) break;

    const sel = $getSelection();
    if ($isRangeSelection(sel)) {
      const selPage = getParentPage(sel.anchor.getNode());

      // If selection is inside the empty first page, move it to the next page
      // before removing the page (prevents a stuck caret and preserves UX).
      if (selPage && selPage.getKey() === first.getKey()) {
        const nextPage = pages[1];
        const nextBody = nextPage?.getBody?.();
        const target = nextBody?.getFirstChild?.() ?? nextBody;
        if (target) {
          target.selectStart?.();
        }
      }
    }

    safeRemovePage(first);
    pages = root.getChildren().filter($isPageNode);
  }
}

function paginateForward(editor) {
  const root = $getRoot();
  let pages = root.getChildren().filter($isPageNode);
  if (pages.length === 0) {
    root.append($createPageNode());
    pages = root.getChildren().filter($isPageNode);
  }

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    const pageEl = editor.getElementByKey(page.getKey());
    const body = page.getBody();
    if (!pageEl || !body) continue;

    const bodyEl = getBodyEl(pageEl);
    if (!bodyEl) continue;

    const maxH = getBodyMaxHeight(bodyEl);
    // On initial mount/refresh, DOM can report 0 heights for a frame.
    // Paginating with maxH=0 will wrongly push all content down creating empty pages.
    if (maxH <= 0) {
      continue;
    }

    while (true) {
      const used = getUsedHeight(editor, page);
      if (used <= maxH) break;

      let next = pages[i + 1];
      if (!next) {
        // Copy header/footer from the current page
        next = $createPageNodeWithHeaderFooter(page);
        root.append(next);
        pages = root.getChildren().filter($isPageNode);
      }

      const children = body.getChildren();
      if (children.length === 0) break;

      const last = children[children.length - 1];
      last.remove();
      prependToBody(next, last);
    }
  }
}

function paginateBackward(editor) {
  const root = $getRoot();
  const pages = root.getChildren().filter($isPageNode);
  if (pages.length <= 1) return;

  for (let i = 0; i < pages.length - 1; i++) {
    const page = pages[i];
    const next = pages[i + 1];

    const pageEl = editor.getElementByKey(page.getKey());
    const body = page.getBody();
    const nextBody = next.getBody();
    if (!pageEl || !body || !nextBody) continue;

    const bodyEl = getBodyEl(pageEl);
    if (!bodyEl) continue;

    const maxH = getBodyMaxHeight(bodyEl);
    if (maxH <= 0) {
      continue;
    }

    while (nextBody.getChildrenSize() > 0) {
      const candidate = nextBody.getFirstChild();
      const h = getBlockOuterHeight(editor, candidate);
      const used = getUsedHeight(editor, page);

      if (used + h <= maxH) {
        candidate.remove();
        body.append(candidate);
      } else {
        break;
      }
    }
  }
}

function cleanupEmptyTrailingPagesAndFixSelection() {
  const root = $getRoot();
  let pages = root.getChildren().filter($isPageNode);
  if (pages.length <= 1) return;

  while (pages.length > 1) {
    const last = pages[pages.length - 1];
    const lastEmpty = isBodyEffectivelyEmpty(last);
    if (!lastEmpty) break;

    const sel = $getSelection();
    const prev = pages[pages.length - 2];
    
    if ($isRangeSelection(sel)) {
      const selPage = getParentPage(sel.anchor.getNode());
      const region = getParentRegionType(sel.anchor.getNode());

      // If selection is in the empty trailing page, we need to move it first
      if (selPage && selPage.getKey() === last.getKey()) {
        // If in body region, preserve user's editing position - don't remove page
        if (region === 'body') {
          break;
        }
        // If in header/footer region, move selection to previous page before removal
        selectEndOfBody(prev);
      }
    }

    safeRemovePage(last);
    pages = root.getChildren().filter($isPageNode);
  }
}

function shouldBreakOnEnter(editor, page, paragraph) {
  const pageEl = editor.getElementByKey(page.getKey());
  if (!pageEl) return false;

  const bodyEl = getBodyEl(pageEl);
  if (!bodyEl) return false;

  const maxH = getBodyMaxHeight(bodyEl);
  if (maxH <= 0) return false;
  const used = getUsedHeight(editor, page);

  const pEl = paragraph ? editor.getElementByKey(paragraph.getKey()) : null;
  const lineH = pEl ? getLineHeightPx(pEl) : 18;

  if (used >= maxH - lineH * 1.05) return true;

  if (pEl) {
    const pBottom = pEl.getBoundingClientRect().bottom;
    const bodyRect = bodyEl.getBoundingClientRect();
    const cs = window.getComputedStyle(bodyEl);
    const bodyBottom = bodyRect.bottom - px(cs.paddingBottom);
    return pBottom >= bodyBottom - lineH * 0.75;
  }

  return false;
}

export default function PaginationPlugin() {
  const [editor] = useLexicalComposerContext();
  const raf = useRef(null);
  const bootstrapRaf = useRef(null);
  const bootstrapTriesLeft = useRef(0);

  function scheduleBootstrapPagination() {
    // Retry pagination after refresh/load when DOM measurements are 0 for a frame.
    if (bootstrapRaf.current != null) return;
    if (bootstrapTriesLeft.current <= 0) {
      bootstrapTriesLeft.current = 12;
    }

    const tick = () => {
      bootstrapRaf.current = null;
      if (bootstrapTriesLeft.current <= 0) return;
      bootstrapTriesLeft.current -= 1;

      let canMeasure = false;
      try {
        const rootEl = editor.getRootElement();
        if (rootEl) {
          const bodyEl = rootEl.querySelector('[data-page-body="true"]');
          if (bodyEl) {
            const maxH = getBodyMaxHeight(bodyEl);
            canMeasure = maxH > 0;
          }
        }
      } catch {
        // ignore
      }

      if (!canMeasure) {
        bootstrapRaf.current = requestAnimationFrame(tick);
        return;
      }

      editor.update(
        () => {
          paginateForward(editor);
          paginateBackward(editor);
          cleanupEmptyTrailingPagesAndFixSelection();
          cleanupEmptyLeadingPagesAndFixSelection();
        },
        {tag: 'pagination'},
      );
    };

    bootstrapRaf.current = requestAnimationFrame(tick);
  }

  useEffect(() => {
    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        // Only take over Enter when we can fully handle it.
        // If selection is not a RangeSelection (e.g. node selection, table/grid selection),
        // returning false lets Lexical's default handlers do the right thing.
        let canHandle = false;
        let regionType = null;
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          canHandle = $isRangeSelection(selection);
          if (canHandle) {
            regionType = getParentRegionType(selection.anchor.getNode());
          }
        });

        if (!canHandle) {
          return false;
        }

        // In header/footer regions, let the default Enter behavior work
        // (just insert a new paragraph, no page-break logic)
        if (regionType === 'header' || regionType === 'footer') {
          return false;
        }

        event?.preventDefault?.();

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          const range = selection;
          const anchorNode = range.anchor.getNode();
          const page = getParentPage(anchorNode);

          if (!page) {
            range.insertParagraph();
            return;
          }

          const paragraph = getParentParagraph(anchorNode);
          const root = $getRoot();
          const pages = root.getChildren().filter($isPageNode);
          const idx = pages.findIndex((p) => p.getKey() === page.getKey());

          if (idx >= 0 && shouldBreakOnEnter(editor, page, paragraph)) {
            const next = ensureNextPage(pages, root, idx);

            const nextBody = next.getBody?.();
            const first = nextBody?.getFirstChild?.() ?? null;
            const onlyOne = (nextBody?.getChildrenSize?.() ?? 0) === 1;

            // New pages are created with a single placeholder empty paragraph.
            // Reuse it instead of inserting an additional empty paragraph.
            if (nextBody && first && onlyOne && isEmptyParagraph(first)) {
              selectParagraphStart(first);
            } else {
              const newPara = $createParagraphNode();
              newPara.append($createTextNode(''));
              prependToBody(next, newPara);
              selectParagraphStart(newPara);
            }

            paginateForward(editor);
            paginateBackward(editor);
            cleanupEmptyTrailingPagesAndFixSelection();
            return;
          }

          range.insertParagraph();

          const sel2 = $getSelection();
          if ($isRangeSelection(sel2)) {
            const newP = getParentParagraph(sel2.anchor.getNode());
            if (newP) selectParagraphStart(newP);
          }
        });

        return true;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    const unregisterUpdate = editor.registerUpdateListener(({tags}) => {
      if (tags != null && tags.has('pagination')) return;

      if (raf.current != null) cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => {
        raf.current = null;
        editor.update(
          () => {
            paginateForward(editor);
            paginateBackward(editor);
            cleanupEmptyTrailingPagesAndFixSelection();
            cleanupEmptyLeadingPagesAndFixSelection();
          },
          {tag: 'pagination'},
        );
      });
    });

    // On refresh/load the first update can occur before layout is measurable;
    // this ensures we still paginate once DOM sizes stabilize.
    scheduleBootstrapPagination();

    return () => {
      unregisterEnter();
      unregisterUpdate();
      if (raf.current != null) cancelAnimationFrame(raf.current);
      if (bootstrapRaf.current != null) cancelAnimationFrame(bootstrapRaf.current);
    };
  }, [editor]);

  return null;
}
